import { useCallback, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { saveStoreData, saveAuthData } from "../../features/login/LoginSlice";
import { apiHandler } from "../../api";
import {
  BarChart3,
  ShoppingCart,
  Users,
  AlertCircle,
  Banknote,
  RefreshCcw,
  FileCog,
  FileText,
  FileX,
  Settings,
  MessageSquare,
  HelpCircle,
  HandCoins,
} from "lucide-react";

// Constants
const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_DELAY = 300;
const PAGE_SIZE = 100;

// Navigation Items
const navigationItems = [
  {
    id: "analytics",
    label: "Analytics",
    icon: BarChart3,
    path: "/",
  },
  {
    id: "payout",
    label: "Payout",
    icon: HandCoins,
    path: "/payout",
  },
  {
    id: "orders",
    label: "Orders",
    icon: ShoppingCart,
    path: "/orders",
  },
  {
    id: "issues-buyer",
    label: "Counter Party Issues",
    icon: Users,
    path: "/issues/buyer",
  },
  {
    id: "issues-seller",
    label: "Reported Issues",
    icon: AlertCircle,
    path: "/issues/seller",
  },
  {
    id: "settlement",
    label: "Settlements",
    icon: Banknote,
    path: "/settlement",
  },
  {
    id: "recons",
    label: "Recons",
    icon: RefreshCcw,
    path: "/recons",
    permission: "RECON_LISTING_VIEW",
  },
  {
    id: "misc-settlements",
    label: "Misc Settlements",
    icon: FileCog,
    path: "/misc-settlements",
  },
  {
    id: "reports",
    label: "Requested Settlements Status",
    icon: FileText,
    path: "/reports",
  },
  {
    id: "catalogue-rejection",
    label: "Catalogue Rejection",
    icon: FileX,
    path: "/catalogue-rejection",
  },
  {
    id: "store-settings",
    label: "Store Settings",
    icon: Settings,
    path: "/store-settings",
  },
  {
    id: "contact-us",
    label: "Contact Us",
    icon: MessageSquare,
    path: "/contact-us",
  },
  {
    id: "faq",
    label: "FAQS",
    icon: HelpCircle,
    path: "/faq",
  },
];

export const useSidebarNavigation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { authData, storeId } = useSelector((state) => state.loginSlice);

  // --- State ---
  const [openSelectStore, setOpenSelectStore] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(storeId);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stores, setStores] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userServices, setUserServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  // --- Refs ---
  const debounceTimer = useRef(null);

  // --- Selectors ---
  const brandName =
    useSelector((state) => state.loginSlice.brandName) ||
    localStorage.getItem("brandName");

  const userData = useSelector((state) => state.loginSlice);
  const tenantName = useSelector((state) => state.loginSlice.tenantName) || "";
  const isAdmin = userData?.isAdmin;

  // --- Derived State ---
  const allStores = [];
  if (stores && typeof stores === "object") {
    Object.values(stores).forEach((storeList) => {
      if (Array.isArray(storeList)) {
        allStores.push(...storeList);
      }
    });
  }

  // --- Handlers ---
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(!sidebarCollapsed);
  }, [sidebarCollapsed]);

  // --- Store Fetching & Search ---
  const fetchStores = useCallback(
    async (searchQuery = "") => {
      if (!isAdmin) return;

      try {
        setLoading(true);
        setError(null);

        const response = await apiHandler({
          url: `https://ondcidentitybackend.clouddeploy.in/api/v1/tenant/getMSNTenants?iPageSize=${PAGE_SIZE}&sSearchByName=${encodeURIComponent(
            searchQuery,
          )}`,
          method: "GET",
          authToken: authData,
          dispatch,
          storeId,
        });

        if (response?.data?.statusCode === 200) {
          setStores(response?.data?.data || {});
          setError(null);
        } else {
          setStores({});
          setError(response?.data?.message);
        }
      } catch (err) {
        setStores({});
        setError("Failed to fetch stores");
      } finally {
        setLoading(false);
      }
    },
    [isAdmin, authData, dispatch, storeId],
  );

  const debouncedSearch = useCallback(
    (searchQuery) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      if (searchQuery === "" || searchQuery.length >= MIN_SEARCH_LENGTH) {
        debounceTimer.current = setTimeout(() => {
          fetchStores(searchQuery);
        }, DEBOUNCE_DELAY);
      }
    },
    [fetchStores],
  );

  const handleStoreSearch = useCallback((e) => {
    const searchQuery = e.target.value;
    setSearch(searchQuery);
    debouncedSearch(searchQuery);
  }, [debouncedSearch]);

  // --- Fetch User Services ---
  const fetchUserServices = useCallback(async () => {
    try {
      setServicesLoading(true);

      let tenantId = isAdmin ? storeId : userData?.tenantId;

      if (!tenantId && authData?.accessToken) {
        try {
          const base64Url = authData.accessToken.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          );
          const decodedToken = JSON.parse(jsonPayload);
          tenantId = decodedToken?.tenantId;
        } catch (decodeError) {
          // Handle decode error silently
        }
      }

      const response = await axios.get(
        "https://ondcidentitybackend.clouddeploy.in/api/v1/auth/getServices",
        {
          headers: {
            Authorization: `Bearer ${authData?.accessToken}`,
            tenantId: tenantId || "",
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.data?.statusCode === 201 || response?.data?.statusCode === 200) {
        const services = response?.data?.data || [];
        setUserServices(services);
      } else {
        setUserServices([]);
      }
    } catch (err) {
      console.error("Error fetching user services:", err);
      setUserServices([]);
    } finally {
      setServicesLoading(false);
    }
  }, [authData, userData, isAdmin, storeId]);

  // --- Initial Fetches ---
  useEffect(() => {
    if (!isAdmin) {
      fetchUserServices();
    }

    if (isAdmin) {
      fetchStores(search);
    }
  }, [isAdmin, fetchStores, search, fetchUserServices]);

  // --- Fetch Services When Store Changes (Admin) ---
  useEffect(() => {
    if (isAdmin && storeId) {
      fetchUserServices();
    }
  }, [isAdmin, storeId, fetchUserServices]);

  // --- Store Selection Handler ---
  const handleChangeStore = useCallback(
    (store) => {
      setSelectedStoreId(store.id);
      dispatch(
        saveStoreData({
          storeId: store.id,
          brandName: store.brand_name,
        }),
      );
      setOpenSelectStore(false);
      navigate("/");
    },
    [dispatch, navigate],
  );

  // --- Service Check Helpers ---
  const hasService = useCallback(
    (serviceId) => {
      return userServices.some((service) => service.service_id === serviceId);
    },
    [userServices],
  );

  const getAllowedServiceIds = useCallback(() => {
    return userServices.map((service) => service.service_id);
  }, [userServices]);

  // --- Filter Navigation Items ---
  const getFilteredNavigationItems = useCallback(() => {
    const allowedServices = getAllowedServiceIds();

    if (allowedServices.length === 0) {
      return navigationItems.filter(
        (item) => item.id === "contact-us" || item.id === "faq"
      );
    }

    return navigationItems.filter((item) => {
      if (item.id === "contact-us" || item.id === "faq") {
        return true;
      }

      if (item.id === "analytics") {
        return (
          hasService(1) ||
          hasService(2) ||
          hasService(5) ||
          hasService(6)
        );
      }

      if (item.id === "orders" || item.id === "store-settings") {
        return hasService(1) || hasService(2);
      }

      if (item.id === "catalogue-rejection") {
        return isAdmin || hasService(3);
      }

      if (
        item.id === "payout" ||
        item.id === "settlement" ||
        item.id === "recons" ||
        item.id === "reports"
      ) {
        return hasService(5);
      }

      if (item.id === "misc-settlements") {
        return isAdmin && hasService(5);
      }

      if (item.id === "issues-buyer" || item.id === "issues-seller") {
        return hasService(6);
      }

      return false;
    });
  }, [getAllowedServiceIds, hasService, isAdmin]);

  // --- Logout Handler ---
  const handleLogout = useCallback(
    (e) => {
      e.preventDefault();
      navigate("/");
      dispatch(saveAuthData(false));
    },
    [dispatch, navigate]
  );

  // --- Tab Active Checker (Service-Aware) ---
  const isTabActive = useCallback(
    (itemPath) => {
      if (itemPath === "/" && (hasService(1) || hasService(2))) {
        return location.pathname === "/";
      }
      else if (itemPath === "/" && hasService(6)) {
        return location.pathname === "/IgmDash";
      }
      else if(itemPath === "/" && hasService(5)) {
        return location.pathname === "/RsfDash";
      }
      return location?.pathname?.startsWith(itemPath) ?? false;
    },
    [location.pathname, hasService]
  );

  return {
    // State
    openSelectStore,
    setOpenSelectStore,
    selectedStoreId,
    search,
    loading,
    error,
    stores,
    sidebarCollapsed,
    userServices,
    servicesLoading,

    // Selectors
    brandName,
    userData,
    tenantName,
    isAdmin,
    allStores,

    // Handlers
    toggleSidebar,
    fetchStores,
    handleStoreSearch,
    handleChangeStore,
    handleLogout,
    isTabActive,
    hasService,
    getFilteredNavigationItems,

    // Navigation Items
    navigationItems,
  };
};
